/*
* 站内信状态管理
*/

import { defineStore } from "pinia";
import axios from "axios";
import api from "@/service/api";

export default defineStore('message', {
    state() {
        return {
            messages: [],           // 未读消息列表
            historyMessages: [],    // 历史消息列表（已读）
            currentMessage: null,   // 当前选中的消息
            visible: false,         // 弹窗是否显示
            loading: false,         // 加载状态
            historyLoading: false,  // 历史消息加载状态
            activeTab: 'unread',    // 当前激活的tab: unread/history
            pollingTimer: null,     // 轮询定时器
            pollingInterval: 10000  // 轮询间隔（10秒）
        }
    },
    getters: {
        // 未读消息数量
        unreadCount: (state) => state.messages.length,
        // 是否有未读消息
        hasUnread: (state) => state.messages.length > 0,
        // 是否可以关闭弹窗（没有未读消息时才能关闭）
        canClose: (state) => state.messages.length === 0
    },
    actions: {
        /**
         * 获取未读消息
         * @param {string} steamid 玩家SteamID
         */
        async fetchUnreadMessages(steamid) {
            if (!steamid) return;
            
            this.loading = true;
            try {
                console.log('获取站内信, steamid:', steamid);
                const res = await axios.get(api.message.getUnread, {
                    params: { steamid }
                });
                
                console.log('站内信响应:', res.data);
                
                if (res.data.code === 200 || res.data.code === 1) {
                    const newMessages = res.data.data || [];
                    console.log('获取到消息数量:', newMessages.length);
                    
                    // 检查是否有新消息
                    if (newMessages.length > this.messages.length) {
                        // 有新消息，自动弹出
                        this.messages = newMessages;
                        if (newMessages.length > 0 && !this.visible) {
                            console.log('有新消息，自动弹出弹窗');
                            this.showModal();
                        }
                    } else {
                        this.messages = newMessages;
                    }
                }
            } catch (error) {
                console.error('获取站内信失败:', error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * 标记消息已读
         * @param {number} messageId 消息ID
         * @param {string} steamid 玩家SteamID
         */
        async markAsRead(messageId, steamid) {
            if (!messageId || !steamid) return false;
            
            try {
                const res = await axios.post(api.message.markRead, {
                    message_id: messageId,
                    steamid: steamid
                });
                
                if (res.data.code === 200 || res.data.code === 1) {
                    // 从列表中移除已读消息
                    this.messages = this.messages.filter(m => m.id !== messageId);
                    
                    // 如果当前选中的消息被标记已读，清除选中
                    if (this.currentMessage && this.currentMessage.id === messageId) {
                        this.currentMessage = null;
                    }
                    
                    // 如果没有更多未读消息，可以关闭弹窗
                    if (this.messages.length === 0) {
                        this.hideModal();
                    }
                    
                    return true;
                }
                return false;
            } catch (error) {
                console.error('标记已读失败:', error);
                return false;
            }
        },

        /**
         * 领取福利
         * @param {number} messageId 消息ID
         * @param {string} steamid 玩家SteamID
         */
        async claimBenefits(messageId, steamid) {
            if (!messageId || !steamid) return false;
            
            try {
                const res = await axios.post(api.message.claimBenefits, {
                    message_id: messageId,
                    steamid: steamid
                });
                
                if (res.data.code === 200 || res.data.code === 1) {
                    // 更新当前消息的福利领取状态
                    const msg = this.messages.find(m => m.id === messageId);
                    if (msg) {
                        msg.benefits_claimed = 1;
                    }
                    if (this.currentMessage && this.currentMessage.id === messageId) {
                        this.currentMessage.benefits_claimed = 1;
                    }
                    return true;
                }
                return false;
            } catch (error) {
                console.error('领取福利失败:', error);
                return false;
            }
        },

        /**
         * 选择消息查看详情
         * @param {object} message 消息对象
         */
        selectMessage(message) {
            this.currentMessage = message;
        },

        /**
         * 获取历史消息（已读）
         * @param {string} steamid 玩家SteamID
         */
        async fetchHistoryMessages(steamid) {
            if (!steamid) return;
            
            this.historyLoading = true;
            try {
                const res = await axios.get(api.message.getHistory, {
                    params: { steamid }
                });
                
                if (res.data.code === 200 || res.data.code === 1) {
                    this.historyMessages = res.data.data || [];
                }
            } catch (error) {
                console.error('获取历史消息失败:', error);
            } finally {
                this.historyLoading = false;
            }
        },

        /**
         * 切换tab
         * @param {string} tab 
         */
        setActiveTab(tab) {
            this.activeTab = tab;
            this.currentMessage = null;
        },

        /**
         * 显示站内信弹窗
         */
        showModal() {
            console.log('显示站内信弹窗, 当前消息数:', this.messages.length);
            this.visible = true;
            // 默认选中第一条消息
            if (this.messages.length > 0 && !this.currentMessage) {
                this.currentMessage = this.messages[0];
            }
        },

        /**
         * 隐藏站内信弹窗（只有没有未读消息时才能关闭）
         */
        hideModal() {
            if (this.messages.length === 0) {
                this.visible = false;
                this.currentMessage = null;
            }
        },

        /**
         * 强制关闭弹窗（用于特殊情况）
         */
        forceHideModal() {
            this.visible = false;
            this.currentMessage = null;
        },

        /**
         * 开始轮询获取新消息
         * @param {string} steamid 玩家SteamID
         */
        startPolling(steamid) {
            if (!steamid) return;
            
            // 先停止之前的轮询
            this.stopPolling();
            
            // 立即获取一次
            this.fetchUnreadMessages(steamid);
            
            // 开始定时轮询
            this.pollingTimer = setInterval(() => {
                this.fetchUnreadMessages(steamid);
            }, this.pollingInterval);
        },

        /**
         * 停止轮询
         */
        stopPolling() {
            if (this.pollingTimer) {
                clearInterval(this.pollingTimer);
                this.pollingTimer = null;
            }
        },

        /**
         * 重置状态
         */
        reset() {
            this.stopPolling();
            this.messages = [];
            this.currentMessage = null;
            this.visible = false;
            this.loading = false;
        }
    }
});
